/**
 * Navigation Sidebar Component
 *
 * Permission-based navigation sidebar with dynamic filtering.
 */

import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNavigation } from "../../hooks/useNavigation";
import { Lock } from "lucide-react";
import UpgradeModal from "../common/UpgradeModal";
import Tooltip from "../common/Tooltip";
import {
  HomeIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  ChartBarIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  ArchiveBoxIcon,
  BeakerIcon,
  IdentificationIcon,
  MapIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  BookOpenIcon,
  MegaphoneIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeSolid,
  UserCircleIcon as UserCircleSolid,
  BuildingOfficeIcon as BuildingOfficeSolid,
  UsersIcon as UsersSolid,
  CalendarDaysIcon as CalendarDaysSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverSolid,
  CreditCardIcon as CreditCardSolid,
  ChartBarIcon as ChartBarSolid,
  BuildingLibraryIcon as BuildingLibrarySolid,
  UserGroupIcon as UserGroupSolid,
  Cog6ToothIcon as Cog6ToothSolid,
  BellIcon as BellSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListSolid,
  ArchiveBoxIcon as ArchiveBoxSolid,
  BeakerIcon as BeakerSolid,
  IdentificationIcon as IdentificationSolid,
  MapIcon as MapSolid,
  PhotoIcon as PhotoSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightSolid,
  DocumentTextIcon as DocumentTextSolid,
  BookOpenIcon as BookOpenSolid,
  MegaphoneIcon as MegaphoneSolid,
  BanknotesIcon as BanknotesSolid,
} from "@heroicons/react/24/solid";

interface NavigationSidebarProps {
  onItemClick?: () => void;
  collapsed?: boolean;
}

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  onItemClick,
  collapsed = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { flatNavigation, totalAccessibleItems } = useNavigation();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedLockedItem, setSelectedLockedItem] = useState<any>(null);
  const [clickBlocked, setClickBlocked] = useState(false);
  const [blockedItemId, setBlockedItemId] = useState<string | null>(null);

  const clearPropertyManagementSessionFilters = () => {
    if (typeof window === "undefined") return;
    // sessionStorage.removeItem("pm:propertyFilters");
    sessionStorage.removeItem("pm:rentalUnitFilters");
  };

  // Filter out items that should be hidden from sidebar
  const sidebarNavigation = flatNavigation.filter(item => !item.hideFromSidebar && item.id !== 'profile');

  const renderIcon = (id: string, active: boolean) => {
    switch (id) {
      case "dashboard":
        return active ? (
          <HomeSolid className="w-5 h-5" />
        ) : (
          <HomeIcon className="w-5 h-5" />
        );
      case "profile":
        return active ? (
          <UserCircleSolid className="w-5 h-5" />
        ) : (
          <UserCircleIcon className="w-5 h-5" />
        );
      case "properties":
        return active ? (
          <BuildingOfficeSolid className="w-5 h-5" />
        ) : (
          <BuildingOfficeIcon className="w-5 h-5" />
        );
      case "tenants":
        return active ? (
          <UsersSolid className="w-5 h-5" />
        ) : (
          <UsersIcon className="w-5 h-5" />
        );
      case "bookings":
        return active ? (
          <CalendarDaysSolid className="w-5 h-5" />
        ) : (
          <CalendarDaysIcon className="w-5 h-5" />
        );
      case "maintenance":
        return active ? (
          <WrenchScrewdriverSolid className="w-5 h-5" />
        ) : (
          <WrenchScrewdriverIcon className="w-5 h-5" />
        );
      case "inventory":
        return active ? (
          <ArchiveBoxSolid className="w-5 h-5" />
        ) : (
          <ArchiveBoxIcon className="w-5 h-5" />
        );
      case "invoices":
        return active ? (
          <DocumentTextSolid className="w-5 h-5" />
        ) : (
          <DocumentTextIcon className="w-5 h-5" />
        );
      case "payments":
        return active ? (
          <CreditCardSolid className="w-5 h-5" />
        ) : (
          <CreditCardIcon className="w-5 h-5" />
        );
      case "expenses":
        return active ? (
          <BanknotesSolid className="w-5 h-5" />
        ) : (
          <BanknotesIcon className="w-5 h-5" />
        );
      case "analytics":
        return active ? (
          <ChartBarSolid className="w-5 h-5" />
        ) : (
          <ChartBarIcon className="w-5 h-5" />
        );
      case "companies":
        return active ? (
          <BuildingLibrarySolid className="w-5 h-5" />
        ) : (
          <BuildingLibraryIcon className="w-5 h-5" />
        );
      case "company_users":
        return active ? (
          <UserGroupSolid className="w-5 h-5" />
        ) : (
          <UserGroupIcon className="w-5 h-5" />
        );
      case "admin":
        return active ? (
          <Cog6ToothSolid className="w-5 h-5" />
        ) : (
          <Cog6ToothIcon className="w-5 h-5" />
        );
      case "notifications":
        return active ? (
          <BellSolid className="w-5 h-5" />
        ) : (
          <BellIcon className="w-5 h-5" />
        );
      case "notification_logs":
        return active ? (
          <ClipboardDocumentListSolid className="w-5 h-5" />
        ) : (
          <ClipboardDocumentListIcon className="w-5 h-5" />
        );
      case "leads":
        return active ? (
          <IdentificationSolid className="w-5 h-5" />
        ) : (
          <IdentificationIcon className="w-5 h-5" />
        );
      case "test_notifications":
        return active ? (
          <BeakerSolid className="w-5 h-5" />
        ) : (
          <BeakerIcon className="w-5 h-5" />
        );
      case "map_demo":
        return active ? (
          <MapSolid className="w-5 h-5" />
        ) : (
          <MapIcon className="w-5 h-5" />
        );
      case "virtual_tours":
        return active ? (
          <PhotoSolid className="w-5 h-5" />
        ) : (
          <PhotoIcon className="w-5 h-5" />
        );
      case "chat":
        return active ? (
          <ChatBubbleLeftRightSolid className="w-5 h-5" />
        ) : (
          <ChatBubbleLeftRightIcon className="w-5 h-5" />
        );
      case "plans":
        return active ? (
          <CreditCardSolid className="w-5 h-5" />
        ) : (
          <CreditCardIcon className="w-5 h-5" />
        );
      case "company_catalogue":
        return active ? (
          <BookOpenSolid className="w-5 h-5" />
        ) : (
          <BookOpenIcon className="w-5 h-5" />
        );
      case "broadcasts":
        return active ? (
          <MegaphoneSolid className="w-5 h-5" />
        ) : (
          <MegaphoneIcon className="w-5 h-5" />
        );
      case "bulk_onboarding":
        return active ? (
          <ClipboardDocumentListSolid className="w-5 h-5" />
        ) : (
          <ClipboardDocumentListIcon className="w-5 h-5" />
        );
      default:
        return <span className="inline-block w-5 h-5 text-center">{"•"}</span>;
    }
  };

  const isActive = (href: string, activeFor?: string[]) => {
    if (href === "") {
      return (
        location.pathname === "/dashboard" ||
        location.pathname === "/dashboard/"
      );
    }
    
    const isMainActive = (
      location.pathname === `/dashboard${href}` ||
      location.pathname.startsWith(`/dashboard${href}/`)
    );
    
    // Check additional activeFor routes
    if (activeFor && activeFor.length > 0) {
      const isActiveForRoute = activeFor.some(route => 
        location.pathname === `/dashboard${route}` ||
        location.pathname.startsWith(`/dashboard${route}/`) ||
        location.pathname.startsWith(`/dashboard${route}?`)
      );
      
      return isMainActive || isActiveForRoute;
    }
    
    return isMainActive;
  };

  return (
    <>
      <nav
        className={`flex-1 mt-5 ${
          collapsed ? "px-1" : "px-2"
        } overflow-y-auto overflow-x-visible`}
        aria-label="Sidebar"
      >
        <div className="space-y-1 pb-4">
        {/* Flat Navigation (no groupings) */}
        {sidebarNavigation.map((item) => {
          const isLocked = item.isLocked;
          const isItemActive = isActive(item.href, item.activeFor);

          const handleClick = (e: React.MouseEvent) => {
            // Prevent rapid double-click on the same item
            if (clickBlocked && blockedItemId === item.id) {
              e.preventDefault();
              return;
            }

            const startBlock = () => {
              setBlockedItemId(item.id);
              setClickBlocked(true);
              setTimeout(() => {
                setClickBlocked(false);
                setBlockedItemId(null);
              }, 1500);
            };

            if (isLocked) {
              e.preventDefault();
              startBlock();
              setSelectedLockedItem(item);
              setShowUpgradeModal(true);
              return;
            }

            // Always handle navigation programmatically to control refresh behavior
            e.preventDefault();
            onItemClick?.();
            startBlock();

            const target = `/dashboard${item.href}`;

            if (item.id === "properties") {
              clearPropertyManagementSessionFilters();
            }

            if (location.pathname === target) {
              // If we're already on the same route:
              if (location.search && location.search.length > 0) {
                // Clear query params and replace current entry
                navigate(target, { replace: true });
              } else {
                // Force a soft refresh by updating state; Outlet is keyed to this
                navigate(target, { replace: true, state: { __refresh: Date.now() } });
              }
            } else {
              // Navigate to the target route (without query params)
              navigate(target);
            }
          };

          const tooltipContent = isLocked ? `${item.name} (Upgrade Required)` : item.name;

          return (
            <Tooltip
              key={item.id}
              content={tooltipContent}
              position="right"
              delay={300}
              disabled={!collapsed}
            >
              <Link
                to={isLocked ? "#" : `/dashboard${item.href}`}
                className={`group relative flex items-center w-full ${
                  collapsed ? "justify-center" : ""
                } px-3 py-2 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  isItemActive
                    ? "bg-purple-100 text-purple-900"
                    : isLocked
                    ? "text-gray-400 hover:bg-gray-50 cursor-pointer"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                onClick={handleClick}
                aria-current={isItemActive ? "page" : undefined}
                aria-disabled={clickBlocked && blockedItemId === item.id}
                tabIndex={clickBlocked && blockedItemId === item.id ? -1 : undefined}
              >
                {/* Active accent bar */}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-y-0 left-0 w-1 rounded-r transition-colors ${
                    isItemActive
                      ? "bg-primary-600"
                      : "bg-transparent group-hover:bg-gray-200"
                  }`}
                />
                <span
                  className={`${collapsed ? "" : "mr-3"} flex-shrink-0 relative`}
                  aria-hidden="true"
                >
                  {renderIcon(item.id, isItemActive)}
                  {/* {isLocked && (
                    <div className="absolute -top-1 -right-1">
                      <Lock className="h-3 w-3 text-red-500 bg-white dark:bg-gray-800 rounded-full" />
                    </div>
                  )} */}
                </span>
                {!collapsed && (
                  <>
                    <span className={`truncate ${isLocked ? 'opacity-70' : ''}`}>{item.name}</span>
                    {isLocked && (
                      <Lock className="ml-auto h-4 w-4 text-red-500" />
                    )}
                    {!isLocked && item.badge && (
                      <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {item.badge}
                      </span>
                    )}
                    {!isLocked && item.isNew && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        New
                      </span>
                    )}
                  </>
                )}
                {collapsed && <span className="sr-only">{item.name}</span>}
              </Link>
            </Tooltip>
          );
        })}

        {/* No Access Message */}
        {totalAccessibleItems === 0 && (
          <div className="px-3 py-4 text-center">
            <div className="text-gray-400 text-sm">
              <div className="mb-2">🔒</div>
              <div>No accessible features</div>
              <div className="text-xs mt-1">Contact your administrator</div>
            </div>
          </div>
        )}
        </div>
      </nav>

      {/* Upgrade Modal - Outside nav for proper positioning */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={selectedLockedItem?.name || "This feature"}
        limitType="feature"
      />
    </>
  );
};

export default NavigationSidebar;
